import { appSessionRevision } from '@/services/appSession';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { gConfig } from '@constants/gConfig';
import { ACTION_TYPES } from '@constants/messageConstants';
import { useWebSocket, sendMessage } from '@hooks/useWebSocket.ts';
import { useCallbacks } from '../hooks/useCallbacks.js';
import WebSocketManager from '@/websocket/WebSocketManager.ts';
import { isAppHome } from '@/services/deviceService';
import { setEnvId } from '@/utils/envIdentity.js';

const PubedEmployees = 'pubEmployees';
export const useManageEmployee = (gAuth, gStripe, setSnackbarValue) => {
  const { t } = useTranslation();
  const init = { count: 0, Items: [] };
  const [employees, setEmployees] = useState(init);
  // Contacts deleted moments ago; a refresh may be empty (no page pushed) or read before the delete settles.
  const recentlyDeleted = useRef(new Map());
  const withoutDeleted = (list) => {
    const now = Date.now();
    recentlyDeleted.current.forEach((until, id) => until < now && recentlyDeleted.current.delete(id));
    return list.filter((item) => !recentlyDeleted.current.has(item.subUUID));
  };
  const [employeeGroups, setEmployeeGroups] = useState(null);
  const [tags, setTags] = useState([]);
  const { registerCallback, invokeCallbacks } = useCallbacks();
  // A completed save may arrive after sign-out, an account switch, or unmount.
  const orderScope = useRef({ key: null, revision: 0 });
  const orderScopeKey = JSON.stringify([
    gAuth.loginState,
    gStripe.customerInfo.subUUID,
    gStripe.customerInfo.companyID,
    gStripe.customerInfo.isAnonymous,
  ]);
  if (orderScope.current.key !== orderScopeKey) {
    orderScope.current.key = orderScopeKey;
    orderScope.current.revision++;
  }
  useEffect(
    () => () => {
      orderScope.current.revision++;
    },
    []
  );

  const getEmployees = useCallback(() => {
    let companyID = gStripe.customerInfo.companyID;
    if (!companyID) return;
    sendMessage({
      action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
      companyID,
      op: 'get',
    });
  }, [gStripe.customerInfo.companyID]);

  const getEmployeeGroups = useCallback(() => {
    let companyID = gStripe.customerInfo.companyID;
    if (!companyID) return;
    sendMessage({
      action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP,
      cid: companyID,
      op: 'getGroups',
    });
  }, [gStripe.customerInfo.companyID]);

  const getTags = useCallback(() => {
    let companyID = gStripe.customerInfo.companyID;
    if (!companyID) return;
    sendMessage({
      action: ACTION_TYPES.BIZ3_MANAGE_ROLE,
      companyID,
      op: 'get',
    });
  }, [gStripe.customerInfo.companyID]);

  const handleEmployeeGroup = useCallback(
    (message) => {
      switch (message.op) {
        case 'getGroups':
          setEmployeeGroups(message.data);
          break;
        case 'add':
          setEmployeeGroups((prevState) => [...prevState, { ...message.data }]);
          break;
        case 'deleteGroups':
          getEmployeeGroups();
          break;
        default:
          break;
      }
    },
    [getEmployeeGroups]
  );

  const employeeError = useCallback(
    (message, fallback) => {
      if (message === 'ユーザーは存在しません') return t('contacts.userNotFound');
      if (message === '必ず【メールアドレス】と【ユーザー名】を含めてください。') return t('contacts.invalidProfile');
      return gStripe.customerInfo.isSesameApp ? t(fallback) : message;
    },
    [gStripe.customerInfo.isSesameApp, t]
  );

  const handleEmployee = useCallback(
    (message) => {
      switch (message.op) {
        case 'get':
          break;
        case 'reportEnv':
          if (message.success && message.data?.environmentId) {
            setEnvId(message.data.environmentId);
          }
          break;
        case PubedEmployees:
          const {
            totalCount,
            data: { list, page },
          } = message.data;
          setEmployees((prevState) => {
            const fresh = withoutDeleted(list);
            const count = totalCount - (list.length - fresh.length);
            if (page === 1) {
              return {
                count,
                Items: [...fresh],
              };
            } else {
              return {
                count: prevState.count,
                Items: [...prevState.Items, ...fresh],
              };
            }
          });
          break;
        case 'add':
          if (!message.success) {
            if (message.message === 'Limit Exceeded') {
              setSnackbarValue({
                open: true,
                msg: t('contacts.limitExceeded'),
              });
            } else {
              setSnackbarValue({
                logScope: 'api/useManageEmployee.handleEmployee',
                severity: 'error',
                open: true,
                msg: employeeError(message.message, 'contacts.addFailed'),
              });
            }
            return;
          }
          getEmployees();
          break;
        case 'update':
          getEmployees();
          break;
        case 'delete':
          if (!message.success) {
            setSnackbarValue({
              logScope: 'api/useManageEmployee.handleEmployee',
              severity: 'error',
              open: true,
              msg: employeeError(message.message, 'contacts.deleteFailed'),
            });
            return;
          }
          getEmployees();
          break;
        default:
          break;
      }
    },
    [setSnackbarValue, getEmployees, employeeError, t]
  );

  const handleRoleResponse = useCallback(
    (message) => {
      if (message.action !== ACTION_TYPES.BIZ3_MANAGE_ROLE) return;
      invokeCallbacks(message);
      if (!message.success) {
        setSnackbarValue({
          logScope: 'api/useManageEmployee.handleRoleResponse',
          severity: 'error',
          open: true,
          msg: message.message,
        });
        return;
      }
      switch (message.op) {
        case 'get':
          setTags(message.data);
          break;
        case 'delete':
          getTags();
          break;
        case 'post':
          getTags();
          break;
        default:
          break;
      }
    },
    [invokeCallbacks, setSnackbarValue, getTags]
  );

  const handleEmployeesResponse = useCallback(
    (message) => {
      invokeCallbacks(message);
      switch (message.action) {
        case ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE:
          handleEmployee(message);
          break;
        case ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP:
          handleEmployeeGroup(message);
          break;
        case ACTION_TYPES.BIZ3_MANAGE_ROLE:
          handleRoleResponse(message);
          break;
        default:
          break;
      }
    },
    [invokeCallbacks, handleEmployee, handleEmployeeGroup, handleRoleResponse]
  );

  const findEmployeeById = useCallback(
    (id) => {
      let find = employees.Items.find((item) => item.subUUID === id);
      return find ?? {};
    },
    [employees.Items]
  );

  const postEmployeeInfo = useCallback(
    (data, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const msgData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
        obj: {
          companyID,
          ...data,
        },
        op: 'update',
      };
      sendMessage(msgData);
      registerCallback(msgData.action, msgData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  // Keep only the current session's display profile across route unmounts.
  const profileScope = `${orderScope.current.revision}:${appSessionRevision()}`;
  const [profile, setProfile] = useState({ scope: profileScope, data: {} });
  const profileVersion = useRef(0);
  if (profile.scope !== profileScope) {
    setProfile({ scope: profileScope, data: {} });
  }
  const currentUserProfile = profile.scope === profileScope ? profile.data : {};

  const getCurrentUserInfo = useCallback(
    (cb, excludeLogins = false) => {
      const revision = orderScope.current.revision;
      const session = appSessionRevision();
      const version = profileVersion.current;
      const msgData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
        op: 'currentInfo',
        ...(excludeLogins ? { excludeLogins: true } : {}),
      };
      registerCallback(msgData.action, msgData.op, (response) => {
        if (revision !== orderScope.current.revision || session !== appSessionRevision()) return;
        if (response.success && response.data && version === profileVersion.current) {
          const { name, email } = response.data;
          setProfile({ scope: `${revision}:${session}`, data: { name, email } });
        }
        cb?.(response);
      });
      sendMessage(msgData);
    },
    [registerCallback]
  );

  const updateCurrentUserName = useCallback(
    (name, cb) => {
      const revision = orderScope.current.revision;
      const session = appSessionRevision();
      postEmployeeInfo({ Name: 'name', Value: name }, (response) => {
        if (revision !== orderScope.current.revision || session !== appSessionRevision()) return;
        if (response.success) {
          // An earlier profile read must not overwrite the newly saved name.
          profileVersion.current++;
          setProfile((previous) => ({
            scope: `${revision}:${session}`,
            data: { ...(previous.scope === `${revision}:${session}` ? previous.data : {}), name },
          }));
        }
        cb?.(response);
      });
    },
    [postEmployeeInfo]
  );

  const removeEmployees = useCallback(
    async (items, cb) => {
      const msgData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
        items,
        op: 'delete',
      };
      sendMessage(msgData);
      registerCallback(msgData.action, msgData.op, (res) => {
        if (res?.success) {
          const ids = new Set(items.map((item) => item.subUUID).filter(Boolean));
          const until = Date.now() + 10000;
          ids.forEach((id) => recentlyDeleted.current.set(id, until));
          setEmployees((prev) => {
            const Items = (prev?.Items || []).filter((item) => !ids.has(item.subUUID));
            return {
              ...prev,
              count: Math.max(0, (prev?.count ?? Items.length) - ((prev?.Items?.length || 0) - Items.length)),
              Items,
            };
          });
        }
        cb && cb(res);
      });
    },
    [registerCallback]
  );

  const addEmployeeGroup = useCallback(
    (item, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP,
        obj: {
          cid: companyID,
          ...item,
        },
        op: 'add',
      };
      sendMessage(messageData);
      registerCallback(ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const postEmployeeGroupInfo = useCallback(
    (item, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP,
        obj: {
          cid: companyID,
          ...item,
        },
        op: 'update',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const removeEmployeeGroups = useCallback(
    (gids) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP,
        objs: gids,
        cid: companyID,
        op: 'deleteGroups',
      };
      sendMessage(messageData);
    },
    [gStripe.customerInfo.companyID]
  );

  const addEmployee = useCallback(
    (items, cb) => {
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
        items,
        op: 'add',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [registerCallback]
  );

  const reorderEmployees = useCallback(
    (items, cb) => {
      const revision = orderScope.current.revision;
      const sessionRevision = appSessionRevision();
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
        items,
        op: 'order',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, (response) => {
        if (revision !== orderScope.current.revision || sessionRevision !== appSessionRevision()) return;
        if (response.success) {
          const positions = new Map(items.map((item, index) => [item.friendUUID, index]));
          setEmployees((previous) =>
            revision !== orderScope.current.revision || sessionRevision !== appSessionRevision()
              ? previous
              : {
                  ...previous,
                  Items: [...previous.Items].sort(
                    (a, b) => (positions.get(a.subUUID) ?? Infinity) - (positions.get(b.subUUID) ?? Infinity)
                  ),
                }
          );
        }
        cb?.(response);
      });
    },
    [registerCallback]
  );

  const postTag = useCallback(
    (data, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_ROLE,
        companyID,
        ...data,
        op: 'post',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const removeTag = useCallback(
    (data, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_ROLE,
        companyID,
        ...data,
        op: 'delete',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const getDeviceGroup = useCallback(
    (gid, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP,
        gid,
        op: 'getBindDeviceGroup',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const addEmployeeInGroup = useCallback(
    (gid, uuids, items, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP,
        cid: companyID,
        gid,
        uuids,
        items,
        op: 'addBindUser',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const removeEmployeeInGroup = useCallback(
    (gid, uuids, items, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const params = items.map((item) => ({
        subUUID: item.subUUID,
      }));
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP,
        cid: companyID,
        gid,
        uuids,
        items: params,
        op: 'removeBindUser',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const removeDeviceGroup = useCallback(
    (data, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP,
        cid: companyID,
        ...data,
        op: 'removeBindDeviceGroup',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const queryByCS = useCallback(
    (keyword, cb) => {
      if (!keyword) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
        keyword,
        op: 'queryByCS',
      };
      let rowDatas = [];
      const handleChunk = (res) => {
        if (res?.success === false) {
          cb?.(res);
          return;
        }
        const { data = {}, totalPage } = res.data ?? {};
        const { totalCount = 0 } = res.data ?? {};
        const { list = [], page = 1 } = data;
        rowDatas = [...rowDatas, ...list];
        const isDone = page === totalPage;
        cb?.({ ...res, data: rowDatas, page, totalPage, totalCount, done: isDone });
        if (!isDone) {
          registerCallback(messageData.action, 'pubQueryByCS', handleChunk);
        }
      };
      sendMessage(messageData);
      registerCallback(messageData.action, 'pubQueryByCS', handleChunk);
    },
    [sendMessage, registerCallback]
  );

  const confirmQueryByCS = useCallback(
    (email, cb) => {
      if (!email) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
        email,
        op: 'confirmQueryByCS',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [sendMessage, registerCallback]
  );

  const getRepairAuthKey = useCallback(
    (cb) => {
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE,
        op: 'getRepairAuthKey',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [sendMessage, registerCallback]
  );

  useEffect(() => {
    if (gAuth.loginState === gConfig.loginState.loginOut) {
      setEmployees(init);
    }
  }, [gAuth.loginState]);

  useEffect(() => {
    if (gStripe.isFromApp) {
      return;
    }
    getEmployees();
  }, [gStripe.isFromApp, gStripe.customerInfo.companyID, getEmployees]);

  useEffect(() => {
    if (!isAppHome || gStripe.customerInfo.isAnonymous !== false) return;
    return WebSocketManager.onConnectionIdChange(() => getEmployees());
  }, [gStripe.customerInfo.isAnonymous, getEmployees]);

  useWebSocket(ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE, handleEmployeesResponse);
  useWebSocket(ACTION_TYPES.BIZ3_MANAGE_EMPLOYEE_GROUP, handleEmployeesResponse);
  useWebSocket(ACTION_TYPES.BIZ3_MANAGE_ROLE, handleEmployeesResponse);

  return {
    employees,
    getEmployees,
    getEmployeeGroups,
    employeeGroups,
    addEmployeeGroup,
    removeEmployeeGroups,
    addEmployee,
    removeEmployees,
    tags,
    getTags,
    removeTag,
    postTag,
    getDeviceGroup,
    addEmployeeInGroup,
    removeEmployeeInGroup,
    removeDeviceGroup,
    postEmployeeInfo,
    getCurrentUserInfo,
    currentUserProfile,
    updateCurrentUserName,
    postEmployeeGroupInfo,
    findEmployeeById,
    reorderEmployees,
    queryByCS,
    confirmQueryByCS,
    getRepairAuthKey,
  };
};
//
